import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Group, Rect, Text } from 'react-konva';
import { useViewport, useProjectState, updateTask, updateQueue, removeQueue, getSharedData } from '@starflow/app/store';
import { RenderImage } from '@starflow/app/components/konva-components/image-render';
import { usePicQueueHooks, useUserIdentificationHooks, useUserVipInfo } from '@shakkerai/pro-commercial';
import { translate } from '@shakkerai/util-i18n';
import { isMainland, isOverseas } from '@shakkerai/pro-utils';
import XingliuLogo from './_legacy/assets/xingliu-logo.png';
import ShakkerLogo from './_legacy/assets/shakker-logo-new.png';
import closeIcon from './_legacy/assets/close.svg';
import { scheduleTaskForUnit } from '../../../helpers/process';
import { getFlowNodeTypes, getFlowNodeOrderList } from '../../../helpers/flow';
import { endAutoRunningFlow } from '../../../helpers/flow';
import { updateVipState, useVipState } from '@shakkerai/pro-store';

// 生成器在画布上面的展示元素
const GAP = 32;

let previousCursor = null;

const setPointerCursor = () => {
  previousCursor = document.body.style.cursor;
  document.body.style.cursor = 'pointer';
};

const resetCursor = () => {
  document.body.style.cursor = previousCursor = 'unset';
};

const TextCompWrap = React.memo(({ text, scale, fill = '#7F7F7F', fontSize, height, y, ...restProps }) => {
  let _fontSize = fontSize || 12;

  if (scale && scale !== 0) {
    _fontSize = _fontSize / scale;
    y = (y || 0) / scale;
  }

  return <Text text={text} fill={fill} align="center" fontSize={_fontSize} y={y} height={16} {...restProps} />;
});

const autoRunningFlowTextMap = {
  generator: translate('gprogress1'),
  inpaint: translate('gprogress2'),
  upscale: translate('gprogress3'),
  outpaint: translate('gprogress4'),
};

const StatusLabel = ({ taskData, allTaskData, width, height, scale, index }) => {
  const { id } = taskData;
  const { unitId } = allTaskData;
  const isSubTask = allTaskData.hasSubTasks;
  const taskStatus = taskData?.status || 'idle';
  const textStatusRef = useRef(null);
  const autoRunningFlowId = useProjectState('autoRunningFlowId');
  const autoRunningFlowNodeId = useProjectState('autoRunningFlowNodeId');
  const nodeOrderList = getFlowNodeOrderList(autoRunningFlowId);
  const autoRunningFlowNodeIndex = nodeOrderList.findIndex((id) => id === autoRunningFlowNodeId);
  const nodeMap = useProjectState('nodeMap');

  const { userVipInfo } = useUserVipInfo();

  const { isOrdinaryUser } = useUserIdentificationHooks(userVipInfo);

  console.log(`[DEBUG] taskData`, taskData);

  const handleRetry = () => {
    scheduleTaskForUnit(unitId);
  };

  const { remindTip, btnText, onInsertClick } = usePicQueueHooks(taskData.taskId);

  const handleOpenVip = useCallback(
    () =>
      updateVipState({
        proPurchaseModalVisible: true,
      }),
    [updateVipState],
  );

  const picVipNode = useMemo(() => {
    if (taskStatus === 'queueing') {
      // 判断是否已经在加速中
      const isAccelerating = taskData?.isAccelerating;
      const topText = `${isAccelerating ? '已加速，' : ''} ${translate('generator_task_queuing')} ${
        taskData.queueIndex || 1
      }/${taskData.queueLength || 1}`;

      return (
        <Group>
          <TextCompWrap width={width} text={topText} scale={scale} />
          <TextCompWrap width={width} text={remindTip} scale={scale} y={20} />
          {!isAccelerating && index === 0 && (
            <TextCompWrap
              width={width}
              height={14}
              y={40}
              scale={scale}
              text={btnText}
              fill="#ffb800"
              textDecoration="underline"
              onMouseEnter={setPointerCursor}
              onMouseLeave={resetCursor}
              onClick={onInsertClick}
            />
          )}
        </Group>
      );
    } else {
      return null;
    }
  }, [remindTip, btnText, taskData, taskStatus, onInsertClick, index, width, scale]);

  const shakkerVipNode = useMemo(() => {
    const isAccelerating = taskData?.isAccelerating;
    if (['generating', 'queueing', 'pending'].includes(taskStatus) && isOrdinaryUser && !isAccelerating) {
      return (
        <Group>
          <TextCompWrap
            width={width}
            height={14}
            y={40}
            scale={scale}
            text="Enjoy faster speed"
            fill="#ffb800"
            textDecoration="underline"
            onMouseEnter={setPointerCursor}
            onMouseLeave={resetCursor}
            onClick={handleOpenVip}
          />
        </Group>
      );
    } else {
      return null;
    }
  }, [taskStatus, handleOpenVip, width, scale, taskData, isOrdinaryUser]);

  const autoRunningFlowText = useMemo(() => {
    const flowNodeTypes = getFlowNodeTypes(autoRunningFlowId);
    let text = autoRunningFlowTextMap[flowNodeTypes[autoRunningFlowNodeIndex]];

    const nodeInfo = nodeMap?.[autoRunningFlowNodeId];
    if (nodeInfo?.data?.label) {
      text = nodeInfo?.data?.label;
    }
    const step = `${autoRunningFlowNodeIndex + 1} / ${flowNodeTypes.length}`;

    return `${step} ${text}`;
  }, [autoRunningFlowId, autoRunningFlowNodeIndex, nodeOrderList, nodeMap]);

  const LoadingText = useMemo(() => {
    if (taskStatus === 'generating') {
      if (autoRunningFlowId) {
        return (
          <Group>
            <TextCompWrap width={width} scale={scale} text={autoRunningFlowText} />
            <TextCompWrap width={width} scale={scale} y={20} text={`${Math.ceil(taskData.progress)}%`} />
          </Group>
        );
      }

      return <TextCompWrap width={width} scale={scale} text={`${Math.ceil(taskData.progress)}%`} />;
    }

    if (taskStatus === 'queueing') {
      // 自动运行的流第二部开始不展示排队中状态
      if (autoRunningFlowId && autoRunningFlowNodeIndex > 0) {
        return <TextCompWrap width={width} scale={scale} text={autoRunningFlowText} />;
      }
      if (isMainland && picVipNode) {
        return picVipNode;
      }
      if (!isMainland && shakkerVipNode) {
        return shakkerVipNode;
      }
      let _text = '';
      if (taskData.queueIndex === 0) {
        _text = '3%';
      } else {
        _text = translate('generator_task_queuing');
      }
      return <TextCompWrap width={width} scale={scale} text={_text} />;
    }

    if (taskStatus === 'error') {
      const errMsg = (taskData?.error || translate('generator_failed_2')).slice(0, 20);

      return (
        <Group>
          <TextCompWrap width={width} scale={scale} text={errMsg} />
          <TextCompWrap
            width={width}
            scale={scale}
            fill="#FF7878"
            textDecoration="underline"
            y={20}
            onClick={!autoRunningFlowId ? handleRetry : endAutoRunningFlow}
            onMouseEnter={setPointerCursor}
            onMouseLeave={resetCursor}
            text={translate(!autoRunningFlowId ? 'generator_regenerate' : 'generator_exitrun')}
          />
        </Group>
      );
    }

    if (autoRunningFlowId && autoRunningFlowNodeIndex > 0) {
      return <TextCompWrap width={width} scale={scale} text={autoRunningFlowText} />;
    }

    return <TextCompWrap width={width} scale={scale} text={translate('generator_start')} />;
  }, [taskStatus, taskData, width, scale, isMainland, picVipNode, shakkerVipNode, autoRunningFlowText]);

  useEffect(() => resetCursor, []);

  if (isSubTask && (taskStatus === 'idle' || taskStatus === 'error')) {
    return null;
  }

  const logo = isOverseas ? ShakkerLogo : XingliuLogo;
  const checkScale = scale || 1;
  const logoWidth = 30 / checkScale;
  const logoHeight = 18 / checkScale;

  const textStatusY = height / 2;

  return (
    <>
      <RenderImage
        x={(width - logoWidth) / 2}
        y={textStatusY - logoHeight * 2}
        width={logoWidth}
        height={logoHeight}
        url={logo}
      />
      <Group ref={textStatusRef} y={textStatusY}>
        {LoadingText}
      </Group>
    </>
  );
};

const Cells = ({ width, height, count, taskData, scale, currentTaskId }) => {
  const cellWidth = (width - (count - 1) * GAP) / count;

  const taskCells = useMemo(() => {
    if (taskData.hasSubTasks && taskData.results?.length > 0) {
      return taskData.results;
    }

    return Array.from({ length: count }).map((index) => ({
      ...taskData,
      index,
    }));
  }, [taskData]);

  return taskCells.map((task, index) => {
    if (taskData.hasSubTasks && task.status === 'error') {
      return null;
    }

    return task.status === 'idle' ? null : (
      <Group
        key={index}
        x={index * (cellWidth + GAP)}
        y={0}
        width={cellWidth}
        height={height}
        clipX={0}
        clipY={0}
        clipWidth={cellWidth}
        clipHeight={height}
      >
        <Rect width={cellWidth} height={height} fill="#fff" />
        <StatusLabel
          currentTaskId={currentTaskId}
          taskData={task}
          allTaskData={taskData}
          width={cellWidth}
          height={height}
          scale={scale}
          index={index}
        />
      </Group>
    );
  });
};

const GeneratorElementInner = ({ scale, currentTaskId }) => {
  const taskMap = useProjectState((state) => state.tasks.map);
  const taskData = taskMap[currentTaskId];

  const handleDragEnd = useCallback((event) => {
    const { x, y } = event.target.attrs;
    updateTask(currentTaskId, { x, y });
  }, []);

  if (!taskData) return null;
  if (taskData.isFlowManualGenerator) return null;

  const { x, y, params, isAuto } = taskData;
  const autoRunningPositionInfo = getSharedData('autoRunningPositionInfo');
  const width = isAuto ? autoRunningPositionInfo?.width ?? taskData.width : taskData.width;
  const height = isAuto ? autoRunningPositionInfo?.height ?? taskData.height : taskData.height;

  if (x == undefined || !width) return null;

  const totalWidth = width * params.count + (params.count - 1) * GAP;

  return (
    <Group x={x} y={y} draggable onDragEnd={handleDragEnd}>
      <Cells
        width={totalWidth}
        height={height}
        count={params.count}
        scale={scale}
        taskData={taskData}
        currentTaskId={currentTaskId}
      />
    </Group>
  );
};

const GeneratorElement = ({ scale }) => {
  const currentTaskId = useProjectState((state) => state.tasks.current);

  if (!currentTaskId) return null;

  return <GeneratorElementInner scale={scale} currentTaskId={currentTaskId} />;
};

const QueueItem = ({ queueData, scale }) => {
  const { x, y, width, height, params } = queueData;

  const handleClose = useCallback(() => {
    removeQueue(queueData.id);
  }, [queueData.id]);

  const handleDragEnd = useCallback(
    (event) => {
      const { x, y } = event.target.attrs;
      updateQueue(queueData.id, { x, y });
    },
    [queueData.id],
  );

  useEffect(() => resetCursor, []);

  if (!width || !height) return null;
  // if (queueData.type !== 'generator') return null;

  const totalWidth = width * params.count + (params.count - 1) * GAP;

  return (
    <Group x={x} y={y} draggable onDragEnd={handleDragEnd}>
      <Cells width={totalWidth} height={height} count={params.count} scale={scale} taskData={queueData} />
      <Group x={totalWidth - 25 / scale} y={5 / scale} width={20 / scale} height={20 / scale}>
        <Rect
          width={20 / scale}
          height={20 / scale}
          fill="#0005"
          cornerRadius={10 / scale}
          onClick={handleClose}
          onMouseEnter={setPointerCursor}
          onMouseLeave={resetCursor}
        />
        <RenderImage
          listening={false}
          x={5 / scale}
          y={5 / scale}
          width={10 / scale}
          height={10 / scale}
          url={closeIcon}
        />
      </Group>
    </Group>
  );
};

const QueueElements = ({ scale }) => {
  const taskQueue = useProjectState((state) => state.tasks.fe_queue);

  if (!taskQueue.length) return null;

  // 排除掉在flow中生成器手动发起的排队任务
  return taskQueue
    .filter((item) => !item.isFlowManualGenerator)
    .map((queueData) => {
      return <QueueItem key={queueData.id} queueData={queueData} scale={scale} />;
    });
};

export const GeneratorElements = React.memo(() => {
  const scale = useViewport().scale;

  return (
    <>
      <QueueElements scale={scale} />
      <GeneratorElement scale={scale} />
    </>
  );
});
